import { useEffect, useState } from 'react';
import type { CantReason, Eye, Person, TechTask } from './api';

/** "Nabil R." — first name and an initial, the way the room says it. */
export function shortName(p: Pick<Person, 'firstName' | 'lastName'> | null | undefined): string {
  if (!p) return '';
  const last = (p.lastName ?? '').trim();
  return `${(p.firstName ?? '').trim()}${last ? ` ${last[0]}.` : ''}`.trim();
}

/** "Dr. Ahmed" — how a technician names the doctor who ordered. */
export function drName(p: Pick<Person, 'firstName'> | null | undefined): string {
  return p?.firstName ? `Dr. ${p.firstName.trim()}` : '';
}

/** "70 M" */
export function ageSex(p: TechTask['patient']): string {
  if (!p) return '';
  return [p.age != null ? String(p.age) : '', p.sex ?? ''].filter(Boolean).join(' ');
}

/** "Nabil R. · 70 M" */
export function patientLine(t: TechTask): string {
  return [shortName(t.patient), ageSex(t.patient)].filter(Boolean).join(' · ');
}

/** Who the result goes to: the ordering doctor, or the outside referrer. */
export function sendsTo(t: TechTask): string {
  if (t.referrerName) return t.referrerName.startsWith('Dr') ? t.referrerName : `Dr. ${t.referrerName}`;
  return drName(t.requestedBy) || 'the doctor';
}

const EYE: Record<string, string> = { right: 'right eye', left: 'left eye', bilateral: 'both eyes' };
export function eyeText(laterality: string | null): string {
  return laterality ? (EYE[laterality] ?? laterality) : '';
}

export function priorityText(t: TechTask): string {
  return t.priority === 'routine' ? '' : t.priority;
}

/** "08:12" */
export function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function secondsUntil(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((new Date(iso).getTime() - now) / 1000));
}

/** A clock that ticks only while something on screen is counting down. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  return now;
}

/* Three-letter months, always — the locale's "Sept" is not the board's "Sep". */
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Sunday 6 Sep" */
export function longDay(d = new Date()): string {
  return `${DAY[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
}

/** "Sun 6 Sep" */
export function shortDay(d = new Date()): string {
  return `${DAY[d.getDay()].slice(0, 3)} ${d.getDate()} ${MON[d.getMonth()]}`;
}

/** "12 Mar" / "12 Mar 2026" */
export function dayMonth(iso: string | null | undefined, withYear = false): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return `${d.getDate()} ${MON[d.getMonth()]}${withYear ? ` ${d.getFullYear()}` : ''}`;
}

export function clock(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
}

const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}`;

/** One refraction value as the board prints it: "−2.25", "90°", "—". */
export function eyeValue(field: keyof Eye, v: number | null | undefined): string {
  if (v == null) return '—';
  return field === 'axis' ? `${v}°` : signed(v);
}

/** "OD −2.25 −0.50 × 90 · OS −2.00" */
export function valuesLine(values: TechTask['values']): string {
  if (!values) return '';
  const eye = (label: string, e?: Eye) => {
    if (!e) return '';
    const parts = [
      e.sphere != null ? signed(e.sphere) : '',
      e.cylinder != null ? signed(e.cylinder) : '',
      e.cylinder != null && e.axis != null ? `× ${e.axis}` : '',
    ].filter(Boolean);
    return parts.length ? `${label} ${parts.join(' ')}` : '';
  };
  return [eye('OD', values.od), eye('OS', values.os)].filter(Boolean).join(' · ');
}

export const CANT_REASONS: Array<{ key: CantReason; label: string; does: string; phone: string }> = [
  { key: 'left', label: 'Patient left / not here', does: 'closes · the doctor is notified', phone: 'Patient left / not here' },
  {
    key: 'machine_down',
    label: 'Machine down',
    does: 'marks the device down — every pending and future order re-routes',
    phone: 'Machine down — offer external dispatch',
  },
  { key: 'cant_cooperate', label: 'Patient can’t cooperate', does: 'closes · best effort attached', phone: 'Patient can’t cooperate — best effort attached' },
  { key: 'prior_step', label: 'A required prior step isn’t done yet', does: 'closes · suggests re-prep with the nurse', phone: 'A required prior step isn’t done yet' },
  { key: 'declined', label: 'Patient declined the test', does: 'closes · the decline is recorded on the order', phone: 'Patient declined the test' },
  { key: 'other', label: 'Other — write the reason', does: 'closes with your line attached — the line is required', phone: 'Other — write the reason' },
];

export function cantLabel(r: CantReason | null): string {
  return CANT_REASONS.find((x) => x.key === r)?.label ?? 'can’t';
}

export function money(n: number | null | undefined): string {
  if (n == null) return '—';
  return `EGP ${Math.round(n).toLocaleString('en-US')}`;
}

/** The worklist test's quiet line under its title. */
export function hintLine(t: TechTask): { text: string; tone: 'mut' | 'brand' | 'red' | 'amb' } | null {
  if (t.state === 'done' || t.state === 'cant') {
    return t.state === 'cant' ? { text: `came back — ${t.cantNote ?? cantLabel(t.cantReason)}`, tone: 'amb' } : null;
  }
  if (t.repeatReason) return { text: `${drName(t.requestedBy) || 'The doctor'}: “${t.repeatReason}”`, tone: 'red' };
  if (t.flow && t.state === 'open') {
    const next = t.flow.next
      ? ` · then ${t.flow.next.label}${t.flow.next.role === 'doctor' && t.requestedBy ? ` — ${drName(t.requestedBy)}` : ''}`
      : '';
    return { text: `flow: ${t.flow.name} · step ${t.flow.step} of ${t.flow.steps}${next}`, tone: 'brand' };
  }
  if (t.testOnly) {
    const who = t.referrerName ? sendsTo(t) : drName(t.requestedBy);
    return { text: `outside request${who ? ` — ${who}` : ''}`, tone: 'amb' };
  }
  if (t.state === 'in_progress' && t.mine) return { text: 'claimed — nobody else can start it', tone: 'mut' };
  if (t.handedFrom && t.state === 'open') return { text: `handed over by ${shortName(t.handedFrom)}`, tone: 'mut' };
  const back =
    t.priority !== 'routine' && t.visitOpen
      ? t.patient?.sex === 'F' ? 'before she returns' : t.patient?.sex === 'M' ? 'before he returns' : 'before they return'
      : '';
  const bits = [back, t.visitOpen ? 'visit open' : ''].filter(Boolean);
  if (bits.length) return { text: bits.join(' — '), tone: 'mut' };
  if (t.indication) return { text: t.indication, tone: 'mut' };
  return null;
}
